import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowLeft, Check, ShieldCheck, Truck, Snowflake, ShoppingCart, BadgeCheck, Sparkles } from "lucide-react";
import { api } from "@/lib/api";
import { findVehicle, CATEGORY_LABELS } from "@/lib/vehicles";
import { useVehicleState } from "@/lib/vehicleStorage";
import { VehicleSelector } from "@/components/VehicleSelector";
import { Stars } from "@/components/Stars";
import { Reveal } from "@/components/Reveal";
import { BeforeAfter } from "@/components/BeforeAfter";
import { Seo } from "@/components/Seo";
import { useLang } from "@/i18n";
import { fmtPrice } from "@/lib/format";

export default function ProductDetail() {
  const { t, lang } = useLang();
  const navigate = useNavigate();
  const { id } = useParams();
  const [product, setProduct] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [active, setActive] = useState(0);
  const [vehicle, setVehicle] = useVehicleState();

  useEffect(() => {
    setProduct(null);
    setNotFound(false);
    setActive(0);
    api
      .get(`/products/${id}`)
      .then((r) => setProduct(r.data))
      .catch(() => setNotFound(true));
  }, [id]);

  if (notFound) {
    return (
      <main className="mx-auto flex min-h-[70vh] max-w-7xl flex-col items-center justify-center px-4 pt-32 text-center">
        <h1 className="font-display text-3xl font-bold text-slate-50">{t("detail.notFound")}</h1>
        <p className="mt-3 text-slate-400">{t("detail.notFoundSub")}</p>
        <Link to="/produits" className="mt-8 rounded-full bg-sky-400 px-6 py-3 text-sm font-bold text-slate-950">
          {t("detail.backCatalog")}
        </Link>
      </main>
    );
  }

  if (!product) {
    return (
      <main className="mx-auto max-w-7xl px-4 pt-40 pb-24 sm:px-8 lg:px-16">
        <div className="grid gap-12 lg:grid-cols-2">
          <div className="glacial-card aspect-[4/3] animate-pulse" />
          <div className="space-y-4">
            <div className="h-6 w-32 animate-pulse rounded bg-slate-800" />
            <div className="h-12 w-3/4 animate-pulse rounded bg-slate-800" />
            <div className="h-24 animate-pulse rounded bg-slate-800" />
          </div>
        </div>
      </main>
    );
  }

  const L = (field) => (lang === "en" ? product[`${field}_en`] || product[field] : product[field]);
  const goCheckout = () => {
    const q = vehicle.marque && vehicle.modele ? `?marque=${vehicle.marque}&modele=${vehicle.modele}&annee=${vehicle.annee}` : "";
    navigate(`/commander${q}`);
  };

  return (
    <main className="mx-auto max-w-7xl px-4 pt-32 pb-24 sm:px-8 lg:px-16">
      <Seo title={`NordiGuard ${product.name} | NordiGuard`} description={L("short")} />

      <Link
        to="/produits"
        data-testid="product-back-link"
        className="group inline-flex items-center gap-2 text-sm text-slate-400 transition-colors hover:text-sky-400"
      >
        <ArrowLeft size={15} className="transition-transform duration-300 group-hover:-translate-x-1" />
        {t("detail.back")}
      </Link>

      <div className="mt-8 grid gap-12 lg:grid-cols-2">
        {/* Galerie */}
        <div>
          <div className="glacial-card relative aspect-[4/3] overflow-hidden" data-testid="product-gallery">
            <AnimatePresence mode="wait">
              <motion.img
                key={product.gallery[active]}
                src={product.gallery[active]}
                alt={`NordiGuard ${product.name} — ${active + 1}`}
                initial={{ opacity: 0, scale: 1.04 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
                className="h-full w-full object-cover"
              />
            </AnimatePresence>
            <span className="absolute top-4 left-4 rounded-full bg-sky-400 px-3 py-1 text-[11px] font-bold uppercase tracking-[0.15em] text-slate-950">
              {L("badge")}
            </span>
          </div>
          <div className="mt-4 grid grid-cols-4 gap-4">
            {product.gallery.map((g, i) => (
              <button
                key={g + i}
                data-testid={`product-thumb-${i}`}
                onClick={() => setActive(i)}
                className={`aspect-[4/3] overflow-hidden rounded-xl border transition-all duration-300 ${
                  active === i ? "border-sky-400 shadow-[0_0_18px_rgba(56,189,248,0.35)]" : "border-slate-700/60 opacity-60 hover:opacity-100"
                }`}
              >
                <img src={g} alt={`Vue ${i + 1}`} className="h-full w-full object-cover" loading="lazy" />
              </button>
            ))}
          </div>
        </div>

        {/* Infos */}
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.25em] text-sky-400">
            {L("category_label")} · {product.fit}
          </p>
          <h1 className="font-display mt-2 text-3xl font-extrabold tracking-tight text-slate-50 sm:text-4xl lg:text-5xl">
            NordiGuard {product.name}
          </h1>
          <p className="mt-3 inline-flex items-center gap-2 rounded-full border border-sky-400/30 bg-sky-500/10 px-4 py-2 text-[11px] font-bold uppercase tracking-[0.12em] text-sky-300 sm:text-xs">
            <Sparkles size={13} className="shrink-0 text-sky-400" />
            {t("marketing.tagline")}
          </p>
          <p className="mt-2 text-sm italic text-slate-400">« {L("tagline")} »</p>
          <div className="mt-4 flex items-center gap-3">
            <Stars value={product.rating} />
            <span className="text-sm text-slate-400">
              {product.rating.toFixed(1)} · {product.reviews} {t("detail.reviewsSuffix")}
            </span>
          </div>

          <div className="mt-6 flex items-baseline gap-3">
            <span className="font-display text-4xl font-extrabold text-slate-50" data-testid="product-price">
              {fmtPrice(product.price, lang)}
            </span>
            <span className="text-sm text-slate-500">{t("detail.perSet")}</span>
          </div>
          <p className="mt-2 inline-flex items-center gap-2 text-xs font-semibold text-amber-300/90">
            <Truck size={13} /> {t("detail.urgency")}
          </p>

          <p className="mt-6 text-base leading-relaxed text-slate-300">{L("description")}</p>

          <ul className="mt-7 space-y-3">
            {L("features").map((f) => (
              <li key={f} className="flex items-start gap-3 text-sm text-slate-300">
                <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-sky-500/15 text-sky-400">
                  <Check size={12} />
                </span>
                {f}
              </li>
            ))}
          </ul>

          <div className="mt-8 flex flex-wrap gap-4">
            <button
              data-testid="order-modal-open-button"
              onClick={goCheckout}
              className="group inline-flex items-center gap-2.5 rounded-full bg-sky-400 px-8 py-4 text-sm font-bold text-slate-950 transition-all duration-300 hover:bg-sky-300 hover:shadow-[0_0_36px_rgba(56,189,248,0.5)]"
            >
              <ShoppingCart size={17} />
              {t("detail.order")}
            </button>
            <a
              href="/contact"
              data-testid="product-ask-question"
              className="inline-flex items-center rounded-full border border-slate-600 px-8 py-4 text-sm font-semibold text-slate-200 transition-all duration-300 hover:border-sky-400/60 hover:text-sky-300"
            >
              {t("detail.question")}
            </a>
          </div>

          <div className="mt-8 grid gap-3 text-xs text-slate-400 sm:grid-cols-3">
            <span className="flex items-center gap-2"><ShieldCheck size={15} className="text-sky-400" /> {t("detail.trust1")}</span>
            <span className="flex items-center gap-2"><Truck size={15} className="text-sky-400" /> {t("detail.trust2")}</span>
            <span className="flex items-center gap-2"><Snowflake size={15} className="text-sky-400" /> {t("detail.trust3")}</span>
          </div>

          {/* Vérification du véhicule */}
          <div className="glacial-card mt-8 p-6" data-testid="vehicle-check-panel">
            <h2 className="font-display text-base font-bold text-slate-50">{t("detail.checkTitle")}</h2>
            <div className="mt-4">
              <VehicleSelector value={vehicle} onChange={setVehicle} />
            </div>
            <VehicleVerdict t={t} vehicle={vehicle} />
          </div>
        </div>
      </div>

      {/* Compatibilité + specs */}
      <div className="mt-20 grid gap-8 lg:grid-cols-2">
        <Reveal>
          <div className="glacial-card h-full p-8">
            <h2 className="font-display text-xl font-bold text-slate-50">{t("detail.compatTitle")}</h2>
            <p className="mt-2 text-sm text-slate-400">{t("detail.compatSub")}</p>
            <div className="mt-5 flex flex-wrap gap-2.5">
              {L("compatibility").map((c) => (
                <span key={c} className="rounded-full border border-sky-500/20 bg-sky-500/5 px-4 py-2 text-xs font-medium text-sky-200">
                  {c}
                </span>
              ))}
            </div>
          </div>
        </Reveal>
        <Reveal delay={0.1}>
          <div className="glacial-card h-full p-8">
            <h2 className="font-display text-xl font-bold text-slate-50">{t("detail.specsTitle")}</h2>
            <dl className="mt-5 divide-y divide-slate-800/80">
              {Object.entries(L("specs")).map(([k, v]) => (
                <div key={k} className="flex items-start justify-between gap-6 py-3">
                  <dt className="text-sm font-semibold text-slate-300">{k}</dt>
                  <dd className="text-right text-sm text-slate-400">{v}</dd>
                </div>
              ))}
            </dl>
          </div>
        </Reveal>
      </div>

      {/* Fabrication sur mesure */}
      <div className="mt-20 grid items-center gap-10 lg:grid-cols-2">
        <Reveal>
          <div className="glacial-card overflow-hidden">
            <img
              src="/images/fabrication-sur-mesure.png"
              alt="Fabrication sur mesure NordiGuard"
              loading="lazy"
              className="w-full object-cover"
              data-testid="fabrication-image"
            />
          </div>
        </Reveal>
        <Reveal delay={0.1}>
          <p className="text-xs font-semibold uppercase tracking-[0.25em] text-sky-400">{t("detail.fabEyebrow")}</p>
          <h2 className="font-display mt-3 text-2xl font-bold tracking-tight text-slate-50 sm:text-3xl">{t("detail.fabTitle")}</h2>
          <p className="mt-4 text-sm leading-relaxed text-slate-400 sm:text-base">{t("detail.fabText")}</p>
        </Reveal>
      </div>

      {/* Avant / Après */}
      <div className="mt-20">
        <Reveal>
          <h2 className="font-display text-2xl font-bold tracking-tight text-slate-50 sm:text-3xl">
            {t("detail.baTitle1")} <span className="text-glacier-gradient">{t("detail.baTitle2")}</span>
          </h2>
          <p className="mt-3 max-w-2xl text-sm leading-relaxed text-slate-400">{t("detail.baSub")}</p>
        </Reveal>
        <Reveal delay={0.1} className="mt-8">
          <BeforeAfter
            before="/images/avant-sale.png"
            after="/images/apres-protege.png"
            beforeLabel={t("beforeAfter.before")}
            afterLabel={t("beforeAfter.after")}
          />
        </Reveal>
      </div>
    </main>
  );
}

const VehicleVerdict = ({ t, vehicle }) => {
  const chosen = vehicle.marque && vehicle.modele ? findVehicle(vehicle.marque, vehicle.modele) : null;
  if (!chosen) return null;
  const label = `${vehicle.marque} ${vehicle.modele}${vehicle.annee ? ` ${vehicle.annee}` : ""}`;

  return (
    <p data-testid="vehicle-check-result" className="mt-4 flex items-start gap-2.5 rounded-xl border border-emerald-500/25 bg-emerald-500/10 p-4 text-sm text-emerald-200">
      <BadgeCheck size={17} className="mt-0.5 shrink-0 text-emerald-400" />
      {t("detail.checkOk", { vehicle: label, category: CATEGORY_LABELS[chosen.category] })}
    </p>
  );
};
